import { useState } from "react";
import { ExternalLink, ArrowRight } from "lucide-react";
import { DIRECTIONS, fmtUsd, fmtAmt, shortAddr, timeAgo } from "@/lib/api";

const FILTERS = [
  { k: "exchange", label: "Exchange flows", match: (t) => t.direction === "inflow" || t.direction === "outflow" },
  { k: "inflow", label: "Inflow", match: (t) => t.direction === "inflow" },
  { k: "outflow", label: "Outflow", match: (t) => t.direction === "outflow" },
  { k: "all", label: "All", match: () => true },
];

const Party = ({ owner, addr }) =>
  owner ? (
    <span className="rounded border border-[#2E3C54] bg-[#161D2A] px-1.5 py-0.5 font-mono text-[11px] text-[#FFD700]">{owner}</span>
  ) : (
    <span className="font-mono text-[11px] text-slate-500">{shortAddr(addr)}</span>
  );

function Item({ t }) {
  const d = DIRECTIONS[t.direction] || DIRECTIONS.wallet;
  return (
    <li
      data-testid={`whale-transfer-item-${t.id}`}
      className="feed-item border-b border-[#1E2738] px-3 py-2.5"
      style={{ borderLeft: `3px solid ${d.color}` }}
    >
      <div className="flex items-center justify-between gap-2">
        <span className="font-mono text-[10px] font-bold tracking-wider" style={{ color: d.color }}>{d.label}</span>
        <span className="font-mono text-[10px] text-slate-500">{timeAgo(t.ts)}</span>
      </div>
      <div className="mt-1 flex items-baseline justify-between gap-2">
        <span className="font-mono text-sm text-slate-100">
          {fmtAmt(t.amount)} <span className="text-slate-400">{t.symbol}</span>
        </span>
        <span className={`font-mono text-sm font-bold ${t.amount_usd >= 1e7 ? "text-[#FFD700]" : "text-slate-200"}`}>{fmtUsd(t.amount_usd)}</span>
      </div>
      <div className="mt-1.5 flex items-center gap-1.5">
        <Party owner={t.from_owner} addr={t.from_address} />
        <ArrowRight className="h-3 w-3 text-slate-600" />
        <Party owner={t.to_owner} addr={t.to_address} />
        <a href={t.explorer_url} target="_blank" rel="noreferrer" data-testid={`tx-link-${t.id}`} className="ml-auto text-slate-500 transition-colors hover:text-[#00E5FF]">
          <ExternalLink className="h-3.5 w-3.5" />
        </a>
      </div>
      <p className="mt-1 font-mono text-[10px] text-slate-600">{d.hint} · {t.chain}{t.source === "whale_alert" ? " · Whale Alert" : ""}</p>
    </li>
  );
}

export default function WhaleFeed({ transfers }) {
  const [filter, setFilter] = useState("exchange");
  const f = FILTERS.find((x) => x.k === filter);
  const rows = transfers.filter(f.match);
  return (
    <section className="panel flex h-[600px] flex-col" data-testid="live-whale-feed">
      <div className="border-b border-[#1E2738] p-3">
        <div className="flex items-center justify-between">
          <h2 className="font-display text-base font-semibold">Live whale tape</h2>
          <span className="font-mono text-[10px] text-slate-500" data-testid="feed-count">{rows.length} shown</span>
        </div>
        <div className="mt-2 flex flex-wrap gap-1">
          {FILTERS.map((x) => (
            <button key={x.k} data-testid={`feed-filter-${x.k}`} onClick={() => setFilter(x.k)} className={`chip !text-[11px] ${filter === x.k ? "active" : ""}`}>
              {x.label}
            </button>
          ))}
        </div>
      </div>
      <ul className="thin-scroll flex-1 overflow-y-auto" data-testid="live-whale-feed-list">
        {rows.map((t) => <Item key={t.id} t={t} />)}
        {!rows.length && (
          <li className="p-6 text-center font-mono text-xs text-slate-500" data-testid="feed-empty">
            Watching the chains… no transfers match yet. Large moves will appear here in real time.
          </li>
        )}
      </ul>
    </section>
  );
}
